/**
 * Currency formatting utilities for FlexPoS system
 * All amounts are formatted in Kenyan Shillings (KES)
 */

/**
 * Format a number as KES currency
 * @param {number|string} amount - The amount to format
 * @param {boolean} includeSymbol - Whether to include the KES symbol (default: true)
 * @param {boolean} useCommas - Whether to include commas for thousands (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, includeSymbol = true, useCommas = true) => {
    const numericAmount = parseFloat(amount);
    
    if (isNaN(numericAmount)) {
        return includeSymbol ? 'KES 0.00' : '0.00';
    }
    
    let formatted;
    if (useCommas) {
        formatted = numericAmount.toLocaleString('en-KE', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        });
    } else {
        formatted = numericAmount.toFixed(2);
    }
    
    return includeSymbol ? `KES ${formatted}` : formatted;
};

/**
 * Format currency for display (same as formatCurrency but more explicit name)
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string with KES prefix
 */
export const displayCurrency = (amount) => {
    return formatCurrency(amount, true);
};

/**
 * Format currency value only (no KES prefix, with commas)
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string without KES prefix but with commas
 */
export const formatCurrencyValue = (amount) => {
    return formatCurrency(amount, false, true);
};

/**
 * Parse currency string back to number
 * @param {string} currencyString - Currency string (with or without KES prefix)
 * @returns {number} Parsed numeric value
 */
export const parseCurrency = (currencyString) => {
    if (typeof currencyString !== 'string') {
        return parseFloat(currencyString) || 0;
    }
    
    // Remove KES prefix and any spaces
    const cleanString = currencyString.replace(/KES\s*/i, '').trim();
    return parseFloat(cleanString) || 0;
};

/**
 * Get the currency symbol
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = () => 'KES';

/**
 * Get the currency code
 * @returns {string} Currency code
 */
export const getCurrencyCode = () => 'KES';

/**
 * Format currency for DataGrid columns (no prefix, with commas)
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string for grid display
 */
export const formatGridCurrency = (amount) => {
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount === 0) return '-';
    
    return numericAmount.toLocaleString('en-KE', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    });
};